import type { Comment } from '@/@types/comment'
import { StoryMediaType, type Story } from '@/@types/story'
import {
	addStoryCommentToCache,
	addStoryCommentReplyToCache,
	getStoryMediaQueryOptions,
	likeStory,
	removeStoryCommentFromCache,
	setStoryLikedInCache,
	unlikeStory,
} from '@/utils/fetchers/story'
import queryClient from '@/utils/queryClient'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { getStoryPictureMedia, getStoryPosterUrl, type StoryMedia } from './storyMedia'

interface UseActiveStoryControllerOptions {
	story?: Story
	initialPlaybackTime?: number
	isMuted: boolean
	onMutedChange: (muted: boolean) => void
	onPlaybackTimeChange: (currentTime: number) => void
}

export default function useActiveStoryController({
	story,
	initialPlaybackTime,
	isMuted,
	onMutedChange,
	onPlaybackTimeChange,
}: UseActiveStoryControllerOptions) {
	const togglingLikeRef = useRef(false)
	const resumePlaybackRef = useRef(false)
	const [commentsStoryId, setCommentsStoryId] = useState<string>()
	const [playback, setPlayback] = useState({ storyId: story?.id, playing: true })
	const [loadingStoryId, setLoadingStoryId] = useState<string>()
	const isPlaying = playback.storyId === story?.id ? playback.playing : true
	const mediaQuery = useQuery({
		...getStoryMediaQueryOptions(story?.id ?? ''),
		enabled: story?.storyType === StoryMediaType.VIDEO,
	})
	const media: StoryMedia | undefined = story
		? story.storyType === StoryMediaType.VIDEO
			? {
					type: 'VIDEO',
					url: mediaQuery.data,
					posterUrl: getStoryPosterUrl(story),
					initialTime: initialPlaybackTime,
					alt: `${story.user.screenName}发布的视频日记`,
					isPlaying,
					isMuted,
					loading: mediaQuery.isFetching,
					errorMessage: mediaQuery.isError ? '视频暂时无法播放' : undefined,
				}
			: getStoryPictureMedia(story)
		: undefined

	const handleLike = () => {
		if (!story || togglingLikeRef.current) return

		const liked = !story.liked
		togglingLikeRef.current = true
		setStoryLikedInCache(queryClient, story.id, liked)
		void (liked ? likeStory(story.id) : unlikeStory(story.id))
			.catch(() => {
				setStoryLikedInCache(queryClient, story.id, !liked)
				toast.error(`${liked ? '点赞' : '取消点赞'}失败，请稍后重试`)
			})
			.finally(() => void (togglingLikeRef.current = false))
	}

	const handleDoubleClickLike = () => {
		if (!story?.liked) handleLike()
	}

	const handlePlaybackBlocked = () => {
		setPlayback({ storyId: story?.id, playing: false })
	}

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.hidden) {
				resumePlaybackRef.current = isPlaying
				setPlayback({ storyId: story?.id, playing: false })
			} else if (resumePlaybackRef.current) {
				resumePlaybackRef.current = false
				setPlayback({ storyId: story?.id, playing: true })
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
	}, [isPlaying, story?.id])

	return {
		commentsOpened: Boolean(story && commentsStoryId === story.id),
		isMediaLoading: Boolean(
			story &&
			!media?.errorMessage &&
			((story.storyType === StoryMediaType.VIDEO && mediaQuery.isFetching) ||
				loadingStoryId !== story.id)
		),
		media,
		handleDoubleClickLike,
		handleLike,
		handleCommentAdded: (comment: Comment) =>
			story && addStoryCommentToCache(queryClient, story.id, comment),
		handleCommentReplyAdded: () => story && addStoryCommentReplyToCache(queryClient, story.id),
		handleCommentDeleted: (comment: Comment) =>
			story && removeStoryCommentFromCache(queryClient, story.id, comment),
		handleMediaLoadingChange: (loading: boolean) =>
			setLoadingStoryId(loading ? undefined : story?.id),
		handlePlaybackBlocked,
		handlePlaybackTimeChange: onPlaybackTimeChange,
		handleRetryMedia:
			story?.storyType === StoryMediaType.VIDEO
				? () => mediaQuery.refetch().then(() => undefined)
				: undefined,
		handleToggleMuted: () => onMutedChange(!isMuted),
		handleTogglePlayback: () => setPlayback({ storyId: story?.id, playing: !isPlaying }),
		handleCommentsClose: () => setCommentsStoryId(undefined),
		handleCommentsOpen: () => setCommentsStoryId(story?.id),
	}
}
